import { useCallback } from 'react';
import PropTypes from 'prop-types';
import NiceModal from '@ebay/nice-modal-react';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { isEmpty } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';

import { getLabelMessage } from '@gef-ui/localization/messages';

import AvatarHead from 'components/atoms/CustomSVGIcon/AvatarHead';
import Sidebar from 'components/atoms/GefMui/Sidebar/Sidebar';
import MODALS from 'constants/modals';
import { fetchCampaignDetail, resetCampaignSettings } from 'features/views/campaignSettings/campaignDetail/actions';
import { checkIfCampaignStatusNew, getDetail } from 'features/views/campaignSettings/campaignDetail/selectors';
import { fetchRegions } from 'features/views/campaignSettings/guarantors/actions';
import { guarantorItemHeight } from 'features/views/campaignSettings/guarantors/constants';
import { getHoveredRegion, getHoveredRegionId } from 'features/views/campaignSettings/guarantors/selectors';
import { templateTypes } from 'features/views/campaignSettings/templates/constants';
import { alphabetSorterCS } from 'utils';

export const GuarantorSidebar = ({ campaignId }) => {
	const dispatch = useDispatch();

	const region = useSelector(getHoveredRegion);
	const hoveredRegionId = useSelector(getHoveredRegionId);
	const campaignDetail = useSelector(getDetail);
	const isStatusNew = useSelector(checkIfCampaignStatusNew);

	const { guarantors = [] } = region;

	const handleSaveTemplate = useCallback(() => {
		NiceModal.show(MODALS.campaignSettings_templates, {
			type: templateTypes.SAVE,
			campaignDetail,
		});
	}, [campaignDetail]);

	const handleTemplateApplied = useCallback(() => {
		dispatch(fetchCampaignDetail(campaignId));
		dispatch(fetchRegions(campaignId));
	}, [dispatch, campaignId]);

	const handleLoadFromTemplate = useCallback(
		() =>
			NiceModal.show(MODALS.campaignSettings_templates, {
				type: templateTypes.LOAD,
				campaignDetail,
				onSuccess: handleTemplateApplied,
			}),
		[campaignDetail, handleTemplateApplied],
	);

	const handleClearSetup = useCallback(() => {
		NiceModal.show(MODALS.lightbox_modal, {
			title: getLabelMessage('lightBox:campaignSettingsClear'),
			onConfirm: () => {
				dispatch(
					resetCampaignSettings(campaignId, () => {
						dispatch(fetchRegions(campaignId));
					}),
				);
			},
		});
	}, [dispatch, campaignId]);

	const checkIfOverflow = (sortedGuarantors) => {
		const sidebarMain = document.getElementsByClassName('Sidebar-main')[0];
		const sidebarMainHeight = sidebarMain.clientHeight;
		const guarantorsLimit = Math.floor(sidebarMainHeight / guarantorItemHeight);
		const overLimit = sortedGuarantors.length - guarantorsLimit;
		let sortedGuarantorsLength = [];
		if (overLimit > 0) {
			sortedGuarantorsLength = sortedGuarantors.slice(0, guarantorsLimit - 1);
			sortedGuarantorsLength.push(`+ ${overLimit + 1} ${getLabelMessage('guarantors:overLimitGuarantors')}`);
		} else {
			sortedGuarantorsLength = sortedGuarantors;
		}
		return sortedGuarantorsLength;
	};

	const sortedGuarantors = guarantors
		.map((g) => `${g.surname || ''} ${g.firstName || ''}`.trim())
		.sort((a, b) => alphabetSorterCS(a, b));

	return (
		<Sidebar title={getLabelMessage('guarantors:header')}>
			{!isEmpty(campaignDetail) && (
				<>
					<Sidebar.Body>
						{hoveredRegionId &&
							checkIfOverflow(sortedGuarantors).map((name, i) => (
								<Sidebar.Section
									key={i}
									sx={{
										height: `${guarantorItemHeight}px`,
										display: 'flex',
										alignItems: 'center',
										backgroundColor: (theme) => theme.palette.background.paper,
										color: 'text.primary',
									}}
									direction="row"
								>
									<AvatarHead className="Sidebar-icon Sidebar-icon--avatarHead" />
									<Typography variant="body2" color="text.primary">
										{name}
									</Typography>
								</Sidebar.Section>
							))}
					</Sidebar.Body>
					<Sidebar.Footer>
						<Button onClick={handleLoadFromTemplate} fullWidth>
							{getLabelMessage('sidebar:loadFromTempalte')}
						</Button>
						<Button onClick={handleSaveTemplate} fullWidth>
							{getLabelMessage('sidebar:saveTemplate')}
						</Button>
						<Button onClick={handleClearSetup} fullWidth disabled={!isStatusNew}>
							{getLabelMessage('sidebar:deleteConfiguration')}
						</Button>
					</Sidebar.Footer>
				</>
			)}
		</Sidebar>
	);
};

GuarantorSidebar.propTypes = {
	campaignId: PropTypes.string,
};

export default GuarantorSidebar;
